import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { listCategories, listPublishedArticles } from "@/lib/blog.functions";
import { BlogSplitView } from "@/components/blog/BlogSplitView";
import { Reveal } from "@/components/Reveal";
import { BookOpen } from "lucide-react";

const SITE = "https://heallexa.com";
const PAGE_SIZE = 24;

export const Route = createFileRoute("/blog/$category")({
  loader: async ({ params }) => {
    await setSeoPageCache();
    const cats = await listCategories();
    const cat = cats.find((c) => c.slug === params.category);
    if (!cat) throw notFound();
    const first = await listPublishedArticles({ data: { category_slug: params.category, limit: PAGE_SIZE, offset: 0 } });
    return { cat, cats, firstPage: first };
  },
  head: ({ loaderData }) => {
    const catName = loaderData?.cat?.name ?? "Healthcare";
    const catSlug = loaderData?.cat?.slug ?? "";
    const catDesc =
      loaderData?.cat?.description ??
      "Healthcare discovery guides from the Heallexa blog.";
    const url = `${SITE}/blog/${catSlug}`;
    const title = catName ? `${catName} | Heallexa Blog` : "Heallexa Blog";

    return {
      meta: [
        { title },
        { name: "description", content: catDesc },
        { property: "og:title", content: title },
        { property: "og:description", content: catDesc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description: catDesc,
            url,
            inLanguage: "en-US",
            isPartOf: {
              "@type": "Blog",
              name: "Heallexa Blog",
              url: `${SITE}/blog`,
              publisher: {
                "@type": "Organization",
                name: "Heallexa",
                url: SITE,
              },
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
                { "@type": "ListItem", position: 3, name: catName, item: url },
              ],
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background"><Navbar /><div className="pt-32 mx-auto max-w-3xl px-6 text-center"><h1 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">Category not found</h1><Link to="/blog" className="text-primary underline mt-4 inline-block text-[14px]">Back to blog</Link></div></div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background"><Navbar /><div className="pt-32 mx-auto max-w-3xl px-6"><p className="text-destructive">{error.message}</p></div></div>
  ),
  component: BlogCategoryPage,
});

function BlogCategoryPage() {
  const { cat, cats, firstPage } = Route.useLoaderData();

  const { data: pages, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["blog-cat", cat.slug],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      listPublishedArticles({ data: { category_slug: cat.slug, limit: PAGE_SIZE, offset: pageParam as number } }),
    getNextPageParam: (last, all) => (last.hasMore ? all.length * PAGE_SIZE : undefined),
    initialData: { pages: [firstPage], pageParams: [0] },
  });

  const { catMap, catSlugMap } = useMemo(() => {
    const m = new Map<string, string>();
    const sm = new Map<string, string>();
    cats.forEach((c: any) => {
      m.set(c.id, c.name);
      sm.set(c.id, c.slug);
    });
    return { catMap: m, catSlugMap: sm };
  }, [cats]);

  const rows = useMemo(() => {
    const all = (pages?.pages ?? []).flatMap((p: any) => p.rows ?? []);
    return all.map((a: any) => ({
      ...a,
      category_name: catMap.get(a.category_id) ?? cat.name,
      category_slug: catSlugMap.get(a.category_id) ?? cat.slug,
    }));
  }, [pages, catMap, catSlugMap, cat.name, cat.slug]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <header className="pt-32 md:pt-28 pb-12 border-b border-border bg-gradient-to-b from-muted/40 to-background">
        <div className="mx-auto max-w-[1400px] px-6">
          <Reveal from="up">
            <Link to="/blog" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1 font-medium">← All Healthcare Topics</Link>
            <div className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-primary font-semibold">
              <BookOpen className="h-3.5 w-3.5" />
              Category Guide
            </div>
            <h1 className="mt-2 text-[clamp(2rem,4.2vw,3.25rem)] font-medium tracking-[-0.035em] leading-[1.05] text-foreground">{cat.name}</h1>
            {cat.description && <p className="mt-3 text-[15px] text-muted-foreground max-w-[720px] leading-relaxed">{cat.description}</p>}
          </Reveal>
        </div>
      </header>

      <section className="mx-auto max-w-[1400px] px-6 py-8">
        <BlogSplitView
          categories={cats}
          articles={rows}
          initialCategorySlug={cat.slug}
          onLoadMore={() => fetchNextPage()}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </section>

      <FooterSection />
    </div>
  );
}

